import Alert from '@storeblocks/alert';
import { CheckboxProps } from '@storeblocks/checkbox-atom';
import AlertInline from '@storeblocks/inline-alert';
import { type FC, PropsWithChildren, useMemo } from 'react';
import Markdown, { Components } from 'react-markdown';
import styled from 'styled-components';

import { useAssignableRolesQuery } from '@/api/assignableRoles/assignableRolesApi';
import { FormCheckbox } from '@/components/Form/FormCheckbox';
import { FormRow } from '@/components/Form/FormRow';
import { Show } from '@/components/Show';
import { useTexts } from '@/hooks/useTexts';

import { sortAssignableRoles } from './utils/sortAssignableRoles';

const CheckboxGroup: FC<PropsWithChildren> = ({ children }) => {
  const texts = useTexts();

  return (
    <Wrapper>
      <Label htmlFor="roles">{texts.roles}</Label>
      <Content>{children}</Content>
    </Wrapper>
  );
};

interface RolesSelectionProps {
  error: string | undefined;
  displayDisclaimer?: boolean;
}

export const RolesSelection: FC<RolesSelectionProps> = ({
  error,
  displayDisclaimer = false,
}) => {
  const assignableRolesQuery = useAssignableRolesQuery();

  const texts = useTexts();

  const roles = useMemo<CheckboxProps[]>(() => {
    if (!assignableRolesQuery.data) {
      return [];
    }

    const sortedRoles = sortAssignableRoles(assignableRolesQuery.data);

    return sortedRoles.map((role) => ({
      id: role,
      value: role,
      /* @ts-expect-error - role description is from FMS and dynamic */
      label: texts.admin.user.roles.label[role],
      description: (
        <Markdown components={components}>
          {/* @ts-expect-error - role description is from FMS and dynamic */}
          {texts.admin.user.roles.description[role]}
        </Markdown>
      ),
      border: true,
    }));
  }, [assignableRolesQuery.data]);

  if (assignableRolesQuery.isError) {
    return (
      <CheckboxGroup>
        <Alert
          variant="error"
          description={texts.admin.user.roles.fetchError}
        />
      </CheckboxGroup>
    );
  }

  if (error) {
    return (
      <CheckboxGroup>
        <AlertInline title={error} variant="error" />
      </CheckboxGroup>
    );
  }

  return (
    <CheckboxGroup>
      <Show when={displayDisclaimer}>
        <Disclaimer>{texts.admin.user.roles.disclaimer}</Disclaimer>
      </Show>

      <FormRow>
        <FormCheckbox name="roles" options={roles} />
      </FormRow>
    </CheckboxGroup>
  );
};

const Wrapper = styled.section`
  display: flex;
  flex-flow: column;
`;

const Content = styled.div`
  margin-top: 8px;
`;

const Label = styled.label`
  color: #282828;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  word-wrap: break-word;
`;

const Disclaimer = styled.p`
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  margin-bottom: 8px;
`;

const components: Partial<Components> = {
  strong: ({ children }) => <StyledStrong>{children}</StyledStrong>,
};

const StyledStrong = styled.strong`
  font-weight: 600;
`;
